// =============================================================================
// API Response Types
// =============================================================================

export interface ApiResponse<T> {
    data: T
}

export interface ApiError {
    error: string
}

export interface PaginatedResponse<T> {
    data: T[]
    pagination: {
        page: number
        limit: number
        total: number
        pages: number
    }
}

// =============================================================================
// User Types
// =============================================================================

// The database stores roles lowercase (schema default is 'user'); older code
// paths use uppercase. Both are accepted here so the type reflects the values
// that actually arrive over the wire. Compare with isAdminRole, never directly.
export type UserRole = 'user' | 'admin' | 'superadmin' | 'USER' | 'ADMIN' | 'SUPERADMIN'

/** Case-insensitive admin check. Mirrors the backend's requireRole. */
export function isAdminRole(role: string | null | undefined): boolean {
    return ['ADMIN', 'SUPERADMIN'].includes((role || '').toUpperCase())
}

export interface User {
    id: number
    email: string
    name: string | null
    role: UserRole
    createdAt: string
    payload?: Record<string, unknown>
}

export interface UserWithStats extends User {
    _count?: {
        predictions: number
    }
}

export interface LoginResponse {
    token: string
    user: {
        id: number
        email: string
        name: string | null
        role: UserRole
    }
}

export interface MeResponse {
    data: User & {
        predictionsCount: number
    }
    tokenExpiringSoon: boolean
}

// =============================================================================
// Team Types
// =============================================================================

export interface Team {
    id: number
    name: string
    shortName: string
    logo: string | null
    season: string
    createdAt: string
    payload?: Record<string, unknown>
}

// =============================================================================
// Matchday Types
// =============================================================================

export type MatchdayStatus = 'PENDING' | 'OPEN' | 'CLOSED' | 'PROCESSING' | 'PROCESSED'

export interface Match {
    matchNumber: number
    homeTeamId: number
    awayTeamId: number
    homeScore?: number | null
    awayScore?: number | null
    matchDate?: string
    played?: boolean
}

export interface Matchday {
    id: number
    matchdayNumber: number
    season: string
    status: MatchdayStatus
    deadline: string | null
    matches: Match[]
    createdAt: string
    updatedAt: string
}

export interface MatchdayWithPrediction extends Matchday {
    userPrediction: UserPrediction | null
}

// =============================================================================
// Prediction Types
// =============================================================================

export interface MatchPrediction {
    matchNumber: number
    homeScore: number
    awayScore: number
    points?: number
    goals?: number
    resultType?: 'exact' | 'sign_diff' | 'sign' | 'wrong'
}

export interface UserPrediction {
    id: number
    predictions: MatchPrediction[]
    processed: boolean
    totalPoints: number
    totalGoals: number
    correctSigns: number
    exactResults: number
}

export interface Prediction {
    id: number
    userId: number
    matchdayId: number
    season: string
    processed: boolean
    totalPoints: number
    totalGoals: number
    correctSigns: number
    exactResults: number
    predictions: MatchPrediction[]
    createdAt: string
    updatedAt: string
    matchday?: {
        matchdayNumber: number
        status: MatchdayStatus
        matches: Match[]
    }
}

// =============================================================================
// Standings Types
// =============================================================================

export interface StandingsEntry {
    id: number
    userId: number
    matchdayNumber: number
    season: string
    positionPoints: number
    positionGoals: number
    totalPoints: number
    totalGoals: number
    payload?: Record<string, unknown>
    user: {
        id: number
        name: string | null
        email: string
    }
    trend?: {
        points: number
        goals: number
    }
}

export interface StandingsHistory {
    matchdayNumber: number
    positionPoints: number
    positionGoals: number
    totalPoints: number
    totalGoals: number
    payload?: Record<string, unknown>
}

export interface UserSummary {
    totalPoints: number
    totalGoals: number
    accuracy: number
    matchdaysPlayed: number
    exactResults: number
    correctSigns: number
    positionPoints: number | null
    positionGoals: number | null
}

// =============================================================================
// Admin Types
// =============================================================================

export interface AdminStats {
    totalUsers: number
    activeUsers: number
    adminUsers: number
    totalMatchdays: number
    processedMatchdays: number
    pendingMatchdays: number
    totalPredictions: number
    recentPredictions: number
    season: string
}

export interface ProcessResult {
    processed: number
    errors: string[]
    standings: number
}

// =============================================================================
// Config Types
// =============================================================================

export interface AppConfig {
    activeSeason?: string
    scoringRules?: {
        exactScore: number
        correctSignDiff: number
        correctSign: number
    }
    maintenanceMode?: boolean
    [key: string]: unknown
}

// =============================================================================
// Automation Types
//
// Mirrors what the scheduler, the lock manager and the scraper report back.
// =============================================================================

export interface ScraperHealth {
    state: string
    consecutiveFailures: number
    lastSuccessAt: string | null
    lastFailureAt: string | null
    lastError: string | null
}

export interface AutomationJob {
    name: string
    displayName: string
    cronExpression: string
    enabled: boolean
    lastRun: string | null
}

export interface AutomationStatus {
    scheduler: {
        isRunning: boolean
        automationEnabled: boolean
        season: string
        pollMode: 'idle' | 'active'
        scraperHealth: ScraperHealth
        jobs: AutomationJob[]
    }
    locks: Record<string, unknown>
    processing: Record<string, unknown>
    recentLogs: Record<string, unknown>
}

export interface AutomationLog {
    id: number
    eventType: string
    severity: 'info' | 'warning' | 'error'
    message: string
    season: string | null
    createdAt: string
    details?: Record<string, unknown> | null
}

export interface ImportResult {
    success: boolean
    matchdays?: number
    matches?: number
    teams?: number
    errors?: string[]
    [key: string]: unknown
}
